import { useEffect } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import clsx from "clsx";
import { GROUPS, SECTIONS } from "@/config/sections";

// 모바일 네비게이션 Drawer (Backoffice 모바일 재설계 P1, 2026-09-17).
// 데스크톱 Sidebar 와 IA 를 이중 관리하지 않는다 — config/sections.jsx(GROUPS/SECTIONS)를
// 그대로 재사용. 데스크톱 Sidebar 의 multi-expand 대신, 모바일은 전 그룹을 펼쳐 한 번에
// 훑게 한다(좁은 화면에서 탭 두 번 여는 비용 제거).
//
// 동작: 왼쪽 slide-in + backdrop, route 이동/backdrop tap/ESC/닫기버튼으로 close,
//       body scroll lock, 현재 route active 표시.
const EXPANDABLE = new Set(["dashboard", "admin"]);

export default function MobileNavDrawer({ open, onClose }) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get("tab");

  // body scroll lock (열려 있는 동안만)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ESC 로 닫기
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* panel */}
      <aside className="absolute left-0 top-0 flex h-full w-[82vw] max-w-[300px] flex-col bg-white shadow-xl">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-ink-100 px-4">
          <span className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-500 text-[13px] font-bold text-white">N</span>
            <span className="text-[15px] font-semibold tracking-tight text-ink-900">
              Nova <span className="font-normal text-ink-400">Backoffice</span>
            </span>
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="메뉴 닫기"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {Object.entries(GROUPS).map(([key, group]) => {
            const isGroupActive = pathname === group.path || pathname.startsWith(group.path + "/");
            const children = EXPANDABLE.has(key) ? SECTIONS.filter((s) => s.group === key) : [];

            return (
              <div key={key} className="mb-1.5">
                <NavLink
                  to={group.path}
                  onClick={onClose}
                  className={() => clsx(
                    "flex items-center gap-2 rounded-lg px-3 py-2.5 text-[15px] font-medium transition-colors",
                    isGroupActive ? "bg-brand-50 text-brand-700" : "text-ink-700 hover:bg-ink-100"
                  )}
                >
                  {group.icon && <group.icon className="h-4 w-4 shrink-0" />}
                  <span className="flex-1 truncate">{group.label}</span>
                </NavLink>

                {children.length > 0 && (
                  <div className="ml-5 mt-0.5 space-y-0.5 border-l border-ink-100 pl-2">
                    {children.map((s, idx) => {
                      const isTabActive = isGroupActive && (currentTab === s.id || (!currentTab && idx === 0));
                      return (
                        <NavLink
                          key={s.id}
                          to={`${group.path}?tab=${s.id}`}
                          onClick={onClose}
                          className={() => clsx(
                            "block rounded-md px-2.5 py-2 text-[13px] transition-colors",
                            isTabActive ? "bg-brand-50 font-semibold text-brand-700" : "text-ink-500 hover:bg-ink-50 hover:text-ink-800"
                          )}
                        >
                          {s.label}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </div>
  );
}
